import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { Col, Row, Form, FormGroup, Input, Label, Button } from "reactstrap";
import { toast } from "react-toastify";
import { Btn, H2, P } from "../../../../AbstractElements";
import axios, { AxiosError } from "axios";
import { baseUrl } from "../../../../Utils/Baseurl/Baseurl";
import { jwtDecode } from "jwt-decode";
import api from "../../../../services/api";

interface CertificateFormData {
  medicinename: string;
  description: string;
  contents: string;
  quotation: FileList | null;
  gmpcertificate: FileList | null;
  productpackagecoverorinsert: FileList | null;
  batchtestingreport: FileList | null;
  // otherdocuments: FileList | null;
  analysiscertificate: FileList | null;
  otherdetails: File[] | null;

}

interface ExistingData {
  id: string;
  medicinename: string;
  description: string;
  certificates: Record<
    string,
    { path: string; filestatus: string } | undefined
  >;
  VerificationStatus?: number;
  status?: number;
  finalstatus?: number;
  otherdetails?: { path: string; filestatus: string }[];
}

export default function MedicalFiles() {
  const { tokenid } = useParams<{ tokenid: string }>();
  const navigate = useNavigate();
  const [contents, setContents] = useState<
    { name: string; quantity: string }[]
  >([
    { name: "", quantity: "" }, // ✅ default one row
  ]);

  const {
    control,
    handleSubmit,
    setValue, // To update field values
    formState: { errors },
  } = useForm<CertificateFormData>();

  const [existingData, setExistingData] = useState<ExistingData | null>(null);
  const [otherDetails, setOtherDetails] = useState<
    { path: string; filestatus: string }[]
  >([]);
  const [deletedCertificates, setDeletedCertificates] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!tokenid) return;
    const fetchDetails = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}medicinedetails/certificates/${tokenid}`
        );
        setExistingData(response.data);
        console.log("Fetched Data:", response.data);

        // Update form values when data is fetched
        setValue("medicinename", response.data?.medicinename || "");
        setValue("description", response.data?.description || "");

        // Set contents if available
        setContents(response.data.contents || []);
        // set the other documents
        if (response.data?.otherdetails) {
          setOtherDetails(response.data.otherdetails);
        }
      } catch (error) {
        console.error("Error fetching medicine details:", error);
      }
    };

    fetchDetails();
  }, [tokenid, setValue]);

  const [updatedCertificates, setUpdatedCertificates] = useState<
    Record<string, File | undefined>
  >({});

  const [deletedOtherDetails, setDeletedOtherDetails] = useState<string[]>([]);

  useEffect(() => {
    if (existingData?.certificates) {
      setUpdatedCertificates(
        Object.fromEntries(
          Object.entries(existingData.certificates).map(([key, value]) => [
            key,
            undefined, // Ensuring no existing file paths are stored in updatedCertificates
          ])
        )
      );
    }
  }, [existingData]);

  const handleFileDelete = async (key: string) => {
    if (!existingData?.id) {
      toast.error("Medicine ID not found!");
      return;
    }
    try {
      const response = await api.post(`${baseUrl}medicinedetails/delete`, {
        id: existingData.id,
        certificateKey: key,
      });
      if (response.status === 200) {
        toast.success("Certificate deleted successfully!");
        setExistingData((prevData) => {
          if (!prevData || !prevData.certificates) return prevData; // Ensure certificates exist

          const updatedCertificates = { ...prevData.certificates };

          // Remove the deleted certificate by its key
          delete updatedCertificates[key];

          // Return updated data with modified certificates
          return {
            ...prevData,
            certificates: updatedCertificates,
          };
        });

        // Add to deletedCertificates
        setDeletedCertificates((prev) => [...prev, key]);
      }
    } catch {
      toast.error("Failed to delete the certificate.");
    }
  };

  useEffect(() => {
    console.log("Final Deleted Certificates List:", deletedCertificates);
  }, [deletedCertificates]); // Runs whenever deletedCertificates updates


  const mandatoryCertificates: (keyof CertificateFormData)[] = [
    "gmpcertificate",
    "analysiscertificate",
    "quotation",
    "productpackagecoverorinsert",
  ];

  const submitForm = async (data: CertificateFormData) => {
    try {
      // Validation checks
      if (!data.medicinename) {
        toast.error("Please fill in the Medicine name field.");
        return;
      }

      if (contents.length === 0) {
        toast.error("Please add at least one content detail.");
        return;
      }

      for (const content of contents) {
        if (!content.name.trim()) {
          toast.error("Content name cannot be empty.");
          return;
        }
        if (
          !content.quantity.trim() ||
          isNaN(Number(content.quantity)) ||
          Number(content.quantity) <= 0
        ) {
          toast.error(
            "Quantity is required. It must be a valid number greater than 0."
          );
          return;
        }
      }

      const token = localStorage.getItem("token");

      if (!token) {
        toast.error("User is not logged in!");
        return;
      }

      const decoded = jwtDecode<{ id: string }>(token);
      const userId = decoded.id;
      if (!userId) {
        toast.error("Invalid token, user ID not found!");
        return;
      }

      const formData = new FormData();
      formData.append("medicinename", data.medicinename);
      formData.append("description", data.description);
      formData.append("userId", userId);
      formData.append("tokenid", tokenid ?? "");
      formData.append("contents", JSON.stringify(contents));

      if (
        existingData?.VerificationStatus === 1 &&
        existingData?.status === 2 &&
        existingData?.finalstatus === 0
      ) {
        formData.append("status", "0");
      }
      if (
        existingData?.VerificationStatus === 1 &&
        existingData?.status === 1 &&
        existingData?.finalstatus === 2
      ) {
        formData.append("finalstatus", "0");
      }

      // Ensure the first four certificates are uploaded
      for (const cert of mandatoryCertificates) {
        const fileList = data[cert] as FileList | null;
        const existingFile = existingData?.certificates?.[cert]?.path;
        if ((!fileList || fileList.length === 0) && !existingFile) {
          toast.error(`Please upload ${labelMap[cert]}.`);
          return;
        }
      }

      // Handle file uploads
      const fileFields: (keyof CertificateFormData)[] = [
        "gmpcertificate",
        "analysiscertificate",
        "quotation",
        "productpackagecoverorinsert",
        // "batchtestingreport",
      ];

      for (const field of fileFields) {
        const fileList = data[field] as FileList | null;

        if (fileList && fileList.length > 0) {
          formData.append(field, fileList[0]);
        } else if (deletedCertificates.includes(field)) {
          formData.append(field, "null");
        } else {
          formData.append(field, "null");
        }
      }

      // Append existing otherDetails to formData
      if (otherDetails.length > 0) {
        otherDetails.forEach((file) => {
          formData.append("existingOtherDetails[]", file.path);
        });
      }

      // Append new uploads (from form data)
      if (data.otherdetails && data.otherdetails.length > 0) {
        for (const file of data.otherdetails) {
          formData.append("otherdetails", file);
        }
      }

      // Optionally, send deleted otherDetails if needed
      if (deletedOtherDetails.length > 0) {
        deletedOtherDetails.forEach((path) => {
          formData.append("deletedOtherDetails[]", path);
        });
      }

      Object.entries(updatedCertificates).forEach(([key, file]) => {
        if (file instanceof File) {
          formData.append(`certificates[${key}]`, file);
        }
      });

      deletedCertificates.forEach((key) => {
        formData.append("deletedCertificates[]", key);
      });

      // console.log("FormData before submission:");
      // Array.from(formData.entries()).forEach(([key, value]) => {
      //   console.log(key, value);
      // });

      // const response = await axios.post(
      //   `${baseUrl}medicinedetails/createdetails`,
      //   formData,
      //   {
      //     headers: {
      //       "Content-Type": "multipart/form-data",
      //       Authorization: `Bearer ${token}`,
      //     },
      //   }
      // );
      console.log("FormData before submission:");
      Array.from(formData.entries()).forEach(([key, value]) => {
        console.log(key, value);
      });

      // ✅ Show confirmation before submission
      const confirmSubmission = window.confirm(
        "Are you sure you have rechecked all your uploads?"
      );

      if (!confirmSubmission) {
        toast.info("Submission cancelled.");
        return;
      }

      const response = await axios.post(
        `${baseUrl}medicinedetails/createdetails`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (
        response.status >= 200 &&
        response.status < 300 &&
        response.data?.message.includes("successfully")
      ) {
        // alert('Medicine details once uploaded can be reuploaded only once..Are you sure all uploads are correct?')
        toast.success("Medical details submitted successfully!");

        setTimeout(() => {
          navigate(`${process.env.PUBLIC_URL}/token`);
        }, 1000);
      } else {
        throw new Error("Unexpected response from the server");
      }
    } catch (error: unknown) {
      console.error("Error submitting the form:", error);

      const axiosError = error as AxiosError<{ message?: string }>;

      if (axiosError.response && axiosError.response.data?.message) {
        toast.error(axiosError.response.data.message);
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }

      if (axiosError.response && axiosError.response.status === 403) {
        toast.error("Your account has been disabled. Please contact support.");
        localStorage.removeItem("token");

        setTimeout(() => {
          navigate(`/login`);
        }, 1000);
      }
    }
  };

  const labelMap: Record<string, string> = {
    gmpcertificate: "GMP Certificate *",
    analysiscertificate: "Analysis Certificate *",
    quotation: "Quotation *",
    productpackagecoverorinsert: "Product Package Cover / Insert *",
    // batchtestingreport: "Batch Testing Report",
    // otherdocuments: "Other Documents",
  };


  // Define a variable to determine if the submit button should be hidden.
  const hideSubmitButton =
    existingData?.VerificationStatus === 1 &&
    existingData?.status === 1 &&
    (existingData?.finalstatus === 0 || existingData?.finalstatus === 1);


  const addContent = () => {
    setContents([...contents, { name: "", quantity: "" }]);
  };

  const updateContent = (
    index: number,
    field: "name" | "quantity",
    value: string
  ) => {
    const updatedContents = [...contents];
    updatedContents[index][field] = value;
    setContents(updatedContents);
  };

  const removeContent = (index: number) => {
    if (contents.length === 1) {
      // If there's only one item, reset it instead of removing
      setContents([{ name: "", quantity: "" }]);
    } else {
      setContents(contents.filter((_, i) => i !== index));
    }
  };

  return (
    <>
      <div className="certificates p-5">
        <Form className="theme-form" onSubmit={handleSubmit(submitForm)}>
          <>
            <H2 className="text-center">Medical Files</H2>
            <P className="text-center">
              {"Add the related files of the medicine"}
            </P>

            {/* Medicine Name */}
            <FormGroup>
              <Row className="align-items-center">
                <Col md={4}>
                  <Label className="mb-0">Medicine Name <span style={{ color: "red" }}>*</span></Label>
                </Col>
                <Col md={8}>
                  <Controller
                    name="medicinename"
                    control={control}
                    render={({ field }) => <Input type="text" {...field} />}
                  />
                </Col>
              </Row>
            </FormGroup>

            {/* Content section */}

            <FormGroup>
              <Row className="align-items-center">
                {/* Label on the left */}
                <Col md={4}>
                  <Label className="mb-0">Content Details <span style={{ color: "red" }}>*</span></Label>
                </Col>
                {/* Add Button on the right */}
                <Col md={8} className="d-flex justify-content-end align-items-center">
                  <Button color="primary" size="md" onClick={addContent}>
                    Add
                  </Button>
                </Col>
              </Row>
            </FormGroup>

            {/* Ensure at least one content field exists */}
            {contents.length === 0 && setContents([{ name: "", quantity: "" }])}

            {/* Render Content Input Fields */}
            {contents.map((content, index) => (
              <FormGroup key={index}>
                <Row className="align-items-center"> {/* Aligns inputs with label and button */}
                  {/* Empty column to match label position */}
                  <Col md={4}></Col>

                  {/* Content Name Input */}
                  <Col md={4} className="d-flex justify-content-end align-items-center">
                    <Input
                      type="text"
                      placeholder="Content Name"
                      value={content.name}
                      onChange={(e) => updateContent(index, "name", e.target.value)}
                    />
                  </Col>

                  {/* Quantity Input */}
                  <Col md={3} className="d-flex justify-content-end align-items-center">
                    <Input
                      type="text"
                      placeholder="Quantity (gms)"
                      value={content.quantity}
                      onChange={(e) => updateContent(index, "quantity", e.target.value)}
                    />
                  </Col>

                  {/* Delete Button */}
                  <Col md={1} className="text-end">
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => removeContent(index)}
                    >
                      Delete
                    </Button>
                  </Col>
                </Row>
              </FormGroup>
            ))}



            {/* Description */}
            <FormGroup>
              <Row className="align-items-center">
                <Col md={4}>
                  <Label className="mb-0">Description </Label>
                </Col>
                <Col md={8}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <textarea {...field} className="form-control" rows={4} />
                    )}
                  />
                </Col>
              </Row>
            </FormGroup>

            {/* Certificates */}
            {Object.keys(labelMap).map((key) => {
              const fileInfo = existingData?.certificates?.[key]; // Get file details
              let fileStatusLabel = "No documents"; // Default status

              if (fileInfo?.filestatus === "1" || fileInfo?.filestatus === "3") {
                fileStatusLabel = "✅ Approved";
              } else if (fileInfo?.filestatus === "2") {
                fileStatusLabel = "❌ Rejected";
              } else if (fileInfo?.filestatus === "0") {
                fileStatusLabel = "⏳ Not Verified";
              }

              const getFileName = (path: string) => path.split("/").pop() || "View File";

              return (
                <FormGroup key={key}>
                  <Row className="align-items-center">
                    <Col md={4}>
                      <Label>
                        {labelMap[key].replace(" *", "")} {/* Remove the * from the string */}
                        <span style={{ color: "red" }}> *</span> {/* Add it separately with red color */}
                      </Label>

                    </Col>

                    <Col md={6}>
                      <div className="d-flex flex-column">
                        {/* Show existing uploaded file */}
                        {fileInfo && (
                          <div className="mb-2 d-flex align-items-center">
                            <a
                              href={`${baseUrl}${fileInfo.path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="me-2"
                            >
                              📄 {getFileName(fileInfo.path)}
                            </a>

                            {fileInfo.filestatus !== "1" && fileInfo.filestatus !== "3" && (
                              <Button
                                color="danger"
                                size="sm"
                                onClick={() => handleFileDelete(key)}
                              >
                                Delete
                              </Button>
                            )}

                          </div>
                        )}

                        {/* Show newly uploaded filename with remove option */}
                        {uploadedFiles[key] && (
                          <div className="mb-2 d-flex align-items-center">
                            <p className="mb-0 text-success me-2">📂 {uploadedFiles[key]}</p>
                            <Button
                              color="warning"
                              size="sm"
                              onClick={() => {
                                setUploadedFiles((prev) => ({
                                  ...prev,
                                  [key]: "", // Remove the filename
                                }));
                                setValue(key as keyof CertificateFormData, null); // Reset form value

                                // Reset file input field by creating a reference
                                const fileInput = document.getElementById(`file-input-${key}`) as HTMLInputElement;
                                if (fileInput) {
                                  fileInput.value = ""; // Clear file input field
                                }
                              }}
                            >
                              Remove
                            </Button>


                          </div>
                        )}

                        {/* File input (only if not approved) */}
                        {fileInfo?.filestatus !== "1" && fileInfo?.filestatus !== "3" && (
                          <Controller
                            name={key as keyof CertificateFormData}
                            control={control}
                            render={({ field }) => (
                              <Input
                                id={`file-input-${key}`} // Assign unique ID
                                type="file"
                                onChange={(e) => {
                                  const files = e.target.files;
                                  if (files && files.length > 0) {
                                    field.onChange(files);
                                    setUploadedFiles((prev) => ({
                                      ...prev,
                                      [key]: files[0].name, // Store filename
                                    }));
                                    setDeletedCertificates((prev) => prev.filter((item) => item !== key));
                                  } else {
                                    field.onChange(null);
                                    setUploadedFiles((prev) => ({
                                      ...prev,
                                      [key]: "",
                                    }));
                                  }
                                }}
                              />
                            )}
                          />

                        )}
                      </div>
                    </Col>

                    <Col md={2}>
                      <p className="mb-0">{fileStatusLabel}</p>
                    </Col>
                  </Row>
                </FormGroup>
              );
            })}


            {/* Other Details (Supporting Documents) */}
            <FormGroup>
              <Row className="align-items-center">
                <Col md={4}>
                  <Label className="mb-0">Supporting Documents</Label>
                </Col>
                <Col md={8}>
                  <Controller
                    name="otherdetails"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => {
                      const fileArray = Array.isArray(value) ? value : []; // Ensure it's an array

                      return (
                        <>
                          {/* File Input for new uploads */}
                          <Input
                            type="file"
                            multiple
                            onChange={(e) => {
                              const newFiles = Array.from(e.target.files || []);
                              const mergedFiles = [...fileArray, ...newFiles]; // Append files correctly
                              onChange(mergedFiles);
                            }}
                            {...field}
                          />

                          {/* Display existing files with "View File" links */}
                          {otherDetails.length > 0 && (
                            <ul className="mt-2">
                              {otherDetails.map((file, index) => {
                                // Extract filename from the file path
                                const fileName = file.path.split("/").pop();

                                return (
                                  <li
                                    key={index}
                                    className="list-group-item d-flex justify-content-between align-items-center"
                                  >
                                    <span>{fileName}</span>
                                    <div className="ms-auto">
                                      <Button
                                        color="danger"
                                        size="sm"
                                        style={{
                                          padding: "5px",
                                          marginBottom: "10px",
                                        }}
                                        onClick={() => {
                                          const removedFile =
                                            otherDetails[index];
                                          setDeletedOtherDetails([
                                            ...deletedOtherDetails,
                                            removedFile.path,
                                          ]); // Track removed
                                          const updatedFiles =
                                            otherDetails.filter(
                                              (_, i) => i !== index
                                            );
                                          setOtherDetails(updatedFiles);
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}

                          {/* Display newly uploaded files (before submission) */}
                          {fileArray.length > 0 && (
                            <ul className="mt-2 list-group">
                              {fileArray.map((file, index) => (
                                <li
                                  key={index}
                                  className="list-group-item d-flex justify-content-between align-items-center"
                                >
                                  <span>{file.name}</span>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    style={{
                                      minWidth: "80px",
                                      textAlign: "center",
                                      padding: "5px 10px",
                                    }}
                                    onClick={() => {
                                      const updatedFiles = fileArray.filter(
                                        (_, i) => i !== index
                                      );
                                      onChange(updatedFiles);
                                    }}
                                  >
                                    <i className="fa fa-trash"></i> Remove
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      );
                    }}
                  />
                </Col>
              </Row>
            </FormGroup>

            {/* Conditionally render the Submit Button */}
            {!hideSubmitButton && (
              <FormGroup className="mb-0">
                <div className="text-center mt-3">
                  <Btn color="primary" className="w-50" type="submit">
                    Submit
                  </Btn>
                </div>
              </FormGroup>
            )}
          </>
        </Form>
      </div>
    </>
  );
}
